import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'
import { useSiteSettings } from '@/siteSettingsContext'

export function SecuritySettings() {
  const { isAdmin } = useAuth()
  const { applySettings, refresh } = useSiteSettings()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [requireMfa, setRequireMfa] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await settingsApi.getSiteSettings()
        if (!cancelled) {
          setRequireMfa(Boolean(data.require_mfa))
          applySettings(data)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load security settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin, applySettings])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const updated = await settingsApi.updateSiteSettings({
        require_mfa: requireMfa,
      })
      applySettings(updated)
      await refresh()
      setRequireMfa(Boolean(updated.require_mfa))
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save security settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsShell title="Security" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Security
          </h1>
          <p className="mt-2 max-w-2xl text-brand-muted">
            Site-wide two-factor authentication default. To let someone sign in
            with password only (for testing), use <strong>Turn off</strong> on
            that account under Users.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-brand-muted">Loading…</p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="max-w-xl space-y-5 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
          >
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={requireMfa}
                onChange={(e) => {
                  setRequireMfa(e.target.checked)
                  setSaved(false)
                }}
              />
              <span>
                <span className="block text-sm font-semibold text-brand">
                  Require 2FA for all accounts
                </span>
                <span className="mt-1 block text-sm text-brand-muted">
                  When enabled, users must enrol an authenticator after sign-in,
                  unless you have turned 2FA off for that user under Users.
                </span>
              </span>
            </label>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && <p className="text-sm text-emerald-700">Saved.</p>}
            </div>
          </form>
        )}
      </div>
    </SettingsShell>
  )
}
